// Live PTZ 方向盤 + Zoom（APP#11, spec 21 §3.8）。
// 僅當 device capability_effective 含 PTZ cluster（0x8060）才渲染（自取 capability，
// 失敗即靜默隱藏——唯讀增益不阻斷 Live 頁）。
//
// 行為（spec 21 §3.8，Live 域）：
//   - 8 向方向盤:pointerdown → MoveRelative("pan,tilt" 相對角度);
//     pointerup/leave/cancel → Stop（無確認）。
//   - Zoom ＋/−:op:set Zoom,值型別依 report 自適應（int 數值 / enum 段選）。
//   - 「移動中/追蹤中」badge:聽 signaling room event（Move/Track 事件）。
// 裝置離線/未串流（ready=false）時整列 disable。

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../lib/ws';
import type { SignalFrame } from '../../lib/ws';
import { apiFetch } from '../../lib/api';
import { opCmd, opSet } from './settings/ops';
import { DEVICE_INCLUDE } from './useDeviceList';
import { canControlDevice, type DeviceSummary } from './deviceModel';
import {
  directionToPanTilt,
  hasPtzCluster,
  nextZoomInt,
  ptzActivityOf,
  PTZ_MOVE_RELATIVE,
  PTZ_STOP,
  PTZ_ZOOM,
  zoomKind,
  type PtzActivity,
  type PtzDirection,
} from './ptz';
import './ptz.css';

// 方向盤格位 → 3×3 grid（中央為 STOP/狀態,非方向）。
const GRID: Array<PtzDirection | null> = ['up-left', 'up', 'up-right', 'left', null, 'right', 'down-left', 'down', 'down-right'];

const ARROW: Record<PtzDirection, string> = {
  'up-left': '↖',
  up: '↑',
  'up-right': '↗',
  left: '←',
  right: '→',
  'down-left': '↙',
  down: '↓',
  'down-right': '↘',
};

/** capability 自取結果:undefined=未知（載入中,不渲染）/ true / false。 */
function usePtzCapability(deviceId: string): boolean | undefined {
  const [has, setHas] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    void apiFetch<DeviceSummary>(`/v3/devices/${encodeURIComponent(deviceId)}`, { query: { include: DEVICE_INCLUDE } })
      .then((res) => {
        if (!alive) return;
        const clusters = (res.data?.capability_effective ?? []).map((c) => c.cluster ?? '');
        // 21 §2.1（APP#137）：無 control 權限 → 整盤隱藏（PTZ op 需 command:control + `control`）。
        setHas(hasPtzCluster(clusters) && !!res.data && canControlDevice(res.data));
      })
      .catch(() => {
        if (alive) setHas(false); // 取不到 → 靜默隱藏
      });
    return () => {
      alive = false;
    };
  }, [deviceId]);
  return has;
}

export function PtzPad({ deviceId, ready }: { deviceId: string; ready: boolean }) {
  const { t } = useTranslation();
  const hasPtz = usePtzCapability(deviceId);
  const [activity, setActivity] = useState<PtzActivity>('idle');
  // Zoom 值型別自適應:首個 report/op:set 回值決定 int vs enum;預設 int 倍率。
  const [zoomValue, setZoomValue] = useState<number | string | null>(null);
  const movingRef = useRef(false);

  // room 事件 → 移動中/追蹤中 badge（spec 21 §3.8）。
  useEffect(() => {
    if (hasPtz !== true) return;
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId) return;
      const eventId = (frame.id ?? frame.event_id ?? (frame.payload as { id?: string; event_id?: string } | undefined)?.id ?? (frame.payload as { event_id?: string } | undefined)?.event_id) as
        | string
        | undefined;
      const act = ptzActivityOf(eventId);
      if (act) setActivity(act);
    });
    return off;
  }, [hasPtz, deviceId]);

  const sendStop = useCallback(() => {
    if (!movingRef.current) return;
    movingRef.current = false;
    void opCmd(deviceId, PTZ_STOP).catch(() => {});
  }, [deviceId]);

  const onDirDown = useCallback(
    (dir: PtzDirection) => {
      if (!ready) return;
      movingRef.current = true;
      void opCmd(deviceId, PTZ_MOVE_RELATIVE, directionToPanTilt(dir)).catch(() => {});
    },
    [deviceId, ready]
  );

  const onZoom = useCallback(
    (delta: number) => {
      if (!ready) return;
      const kind = zoomKind(zoomValue);
      if (kind === 'enum') {
        // enum Zoom:在已知段位間移動（值取自 report;此處以 0/1 近/遠示意）。
        const cur = typeof zoomValue === 'number' ? zoomValue : 0;
        const next = delta > 0 ? 1 : 0;
        if (next === cur) return;
        setZoomValue(next);
        void opSet(deviceId, PTZ_ZOOM, next).catch(() => {});
        return;
      }
      // int（含未知預設）:倍率 stepper。
      const cur = typeof zoomValue === 'number' ? zoomValue : 1;
      const next = nextZoomInt(cur, delta);
      if (next === cur) return;
      setZoomValue(next);
      void opSet(deviceId, PTZ_ZOOM, next).catch(() => {});
    },
    [deviceId, ready, zoomValue]
  );

  // 卸載/離開串流確保補送 Stop（避免裝置卡在持續轉動）。
  useEffect(() => {
    if (!ready) sendStop();
  }, [ready, sendStop]);
  useEffect(() => () => sendStop(), [sendStop]);

  if (hasPtz !== true) return null;

  const zoomLabel = typeof zoomValue === 'number' ? `${zoomValue}×` : typeof zoomValue === 'string' ? zoomValue : '—';

  return (
    <div className="ptz-pad" role="group" aria-label={t('devices.ptzGroupLabel')} data-testid="ptz-pad">
      <div className="ptz-wheel">
        {GRID.map((dir, i) =>
          dir ? (
            <button
              key={dir}
              className="ptz-dir"
              data-testid={`ptz-${dir}`}
              disabled={!ready}
              aria-label={t(`devices.ptzMove_${dir}`)}
              onPointerDown={(e) => {
                e.preventDefault();
                e.currentTarget.setPointerCapture?.(e.pointerId);
                onDirDown(dir);
              }}
              onPointerUp={sendStop}
              onPointerLeave={sendStop}
              onPointerCancel={sendStop}
            >
              <span aria-hidden="true">{ARROW[dir]}</span>
            </button>
          ) : (
            <span key={`center-${i}`} className="ptz-center" aria-hidden="true">
              ●
            </span>
          )
        )}
      </div>

      <div className="ptz-zoom" role="group" aria-label={t('devices.ptzZoomGroupLabel')}>
        <button className="btn-ghost lv-zoom-btn" data-testid="ptz-zoom-out" disabled={!ready} aria-label={t('devices.zoomOut')} onClick={() => onZoom(-1)}>
          −
        </button>
        <span className="ptz-zoom-level" data-testid="ptz-zoom-level" aria-live="polite">
          {zoomLabel}
        </span>
        <button className="btn-ghost lv-zoom-btn" data-testid="ptz-zoom-in" disabled={!ready} aria-label={t('devices.zoomIn')} onClick={() => onZoom(1)}>
          ＋
        </button>
      </div>

      {activity !== 'idle' && (
        <span className="ptz-badge" role="status" data-testid="ptz-badge">
          <span aria-hidden="true">{activity === 'tracking' ? '🎯' : '🧭'}</span>
          {activity === 'tracking' ? t('devices.ptzTracking') : t('devices.ptzMoving')}
        </span>
      )}
    </div>
  );
}
